import React, { useState, lazy, Suspense, useEffect } from 'react';
import { 
  initialize, isInitialized, getUserPermissions, getTheme, 
  initializeTheme, getThemeMode 
} from '@payprompt/api-sdk';
import { BrowserRouter as Router, Routes, Route, useSearchParams, useLocation } from 'react-router-dom';
import BottomNavigation from './components/BottomNavigation';
import { applyThemeToCSS } from './utils/themeUtils';

const Coupons = lazy(() => import('./components/ledger/Coupons'));

// Lazy load components for code splitting
const Dashboard = lazy(() => import('./components/Dashboard/Home'));
const Tasks = lazy(() => import('./components/tasks/TaskRoutes'));
const Ledger = lazy(() => import('./components/ledger/Ledger'));
const Profile = lazy(() => import('./components/Profile'));
const Targets = lazy(() => import('./components/target/Targets'));
const Team = lazy(() => import('./components/team/Team'));
const TeamMemberPerformance = lazy(() => import('./components/team/TeamMemberPerformance'));
const Programs = lazy(() => import('./components/Programs'));
const CouponRedemption = lazy(() => import('./components/ledger/CouponRedemption'));
const RedeemPoints = lazy(() => import('./components/ledger/RedeemPoints'));
const RecipientDetails = lazy(() => import('./components/ledger/RecipientDetails'));
const Withdrawal = lazy(() => import('./components/ledger/Withdrawal'));

// Loading component for suspense fallback
const LoadingComponent = () => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <img src="/assets/gif/loading.gif" alt="Loading..." style={{ width: '40px', height: '40px' }} />
  </div>
);

// Apply theme on app initialization
const AppInitializer = () => {
  useEffect(() => {
    // Initialize theme from localStorage
    initializeTheme();
    
    // Apply theme when component mounts
    applyThemeToCSS();
    
    // Also set the data-theme attribute
    const themeMode = getThemeMode();
    document.documentElement.setAttribute('data-theme', themeMode);

    // Also apply theme on window load to ensure it's applied after CSS loads
    const handleLoad = () => {
      setTimeout(() => {
        applyThemeToCSS();
        // Also set the data-theme attribute on load
        const currentThemeMode = getThemeMode();
        document.documentElement.setAttribute('data-theme', currentThemeMode);
      }, 100);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  return null;
};

// Hook to detect standalone mode from URL parameters
const useStandaloneMode = () => {
  const [searchParams] = useSearchParams();
  
  return {
    isStandalone: searchParams.get('standalone') === 'true',
    screen: searchParams.get('screen') || 'dashboard',
    token: searchParams.get('token')
  };
};

// Standalone app with full routing support
interface StandaloneAppProps {
  token: string;
  initialScreen?: string;
}

const StandaloneApp: React.FC<StandaloneAppProps> = ({ token, initialScreen = 'dashboard' }) => {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initSDK = async () => {
      try {
        // Validate token format
        if (!token || token.trim() === '') {
          throw new Error('No token provided');
        }

        // Initialize SDK with the token from URL
        await initialize(token);
        setInitialized(true);
      } catch (err) {
        console.error('Failed to initialize in standalone mode:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    if (token && !initialized) {
      initSDK();
    }
  }, [token, initialized]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Authentication Failed</h2>
        <p>{error}</p>
        <p>Please check your token and try again.</p>
      </div>
    );
  }

  if (!initialized) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Initialization Failed</h2>
        <p>Unable to initialize the application</p>
      </div>
    );
  }

  // In standalone mode, we still use Router but without BottomNavigation
  // This allows internal navigation to work (e.g., Ledger → Withdrawal)
  return (
    <div className="standalone-container">
      <StandaloneRoutes initialScreen={initialScreen} />
    </div>
  );
};

// Routes component for standalone mode - supports full internal navigation
const StandaloneRoutes: React.FC<{ initialScreen: string }> = ({ initialScreen }) => {
  const location = useLocation();
  
  // Mock permissions for standalone mode
  const showTaskList = true;
  const showTeam = true;
  const showLedger = true;
  const showTarget = true;

  // Redirect to initial screen if at root
  useEffect(() => {
    if (location.pathname === '/' && initialScreen !== 'dashboard') {
      // You could implement a redirect here if needed
      // But with React Router, we can just render the appropriate route
    }
  }, [location.pathname, initialScreen]);

  return (
    <Suspense fallback={<LoadingComponent />}>
      <Routes>
        <Route path="/" element={
          initialScreen === 'dashboard' ? <Dashboard /> :
          initialScreen === 'tasks' ? <Tasks /> :
          initialScreen === 'ledger' ? <Ledger /> :
          initialScreen === 'targets' ? <Targets /> :
          initialScreen === 'team' ? <Team /> :
          initialScreen === 'team-performance' ? <TeamMemberPerformance /> :
          initialScreen === 'programs' ? <Programs /> :
          initialScreen === 'profile' ? <Profile /> :
          initialScreen === 'coupons' ? <Coupons /> :
          initialScreen === 'coupon-redemption' ? <CouponRedemption /> :
          initialScreen === 'redeem-points' ? <RedeemPoints /> :
          initialScreen === 'recipient-details' ? <RecipientDetails /> :
          initialScreen === 'withdrawal' ? <Withdrawal /> :
          <Dashboard />
        } />
        
        {/* All routes available for internal navigation */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tasks/*" element={showTaskList ? <Tasks /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Tasks</h2></div>} />
        <Route path="/targets" element={showTarget ? <Targets /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Targets</h2></div>} />
        <Route path="/team" element={showTeam ? <Team /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Team</h2></div>} />
        <Route path="/team-member-performance" element={showTeam ? <TeamMemberPerformance /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Team Member Performance</h2></div>} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/ledger" element={showLedger ? <Ledger /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Ledger</h2></div>} />
        <Route path="/coupon-redemption" element={<CouponRedemption />} />
        <Route path="/redeem-points" element={<RedeemPoints />} />
        <Route path="/recipient-details" element={<RecipientDetails />} />
        <Route path="/withdrawal" element={<Withdrawal />} />
        <Route path="/coupons" element={<Coupons />} />
        <Route path="/profile" element={<Profile />} />
        
       
      </Routes>
    </Suspense>
  );
};

// Normal authenticated app with navigation
const AuthenticatedApp: React.FC = () => {
  const [userPermissions, setUserPermissions] = useState<any>(null);

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const permissions = await getUserPermissions();
        setUserPermissions(permissions);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };

    if (isInitialized()) {
      fetchPermissions();
    }
  }, []);

  // Mock permissions for demo purposes - in a real app, use actual permissions
  const showTaskList = userPermissions?.permissions?.includes('TASKLIST_DASHBOARD') ?? true;
  const showTeam = userPermissions?.permissions?.includes('TEAMLEAD_DASHBOARD') ?? true;
  const showLedger = userPermissions?.permissions?.includes('ACCOUNTING') ?? true;
  const showTarget = userPermissions?.permissions?.includes('PERFORMANCE_DASHBOARD') ?? true;

  return (
    <div className="app-main-container">
      {/* Main Content Area - Takes up remaining space */}
      <main className="app-content-area">
        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tasks/*" element={showTaskList ? <Tasks /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Tasks</h2></div>} />
            <Route path="/targets" element={showTarget ? <Targets /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Targets</h2></div>} />
            <Route path="/team" element={showTeam ? <Team /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Team</h2></div>} />
            <Route path="/team-member-performance" element={showTeam ? <TeamMemberPerformance /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Team Member Performance</h2></div>} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/ledger" element={showLedger ? <Ledger /> : <div style={{ padding: '20px', textAlign: 'center' }}><h2>You don't have permission to access Ledger</h2></div>} />
            <Route path="/coupon-redemption" element={<CouponRedemption />} />
            <Route path="/redeem-points" element={<RedeemPoints />} />
            <Route path="/recipient-details" element={<RecipientDetails />} />
            <Route path="/withdrawal" element={<Withdrawal />} />
            <Route path="/coupons" element={<Coupons />} />
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </Suspense>
      </main>

      <BottomNavigation
        showTaskList={showTaskList}
        showTeam={showTeam}
        showLedger={showLedger}
        showTarget={showTarget}
      />
    </div>
  );
};

// Login component for normal mode
const LoginScreen: React.FC<{ onLogin: (token: string) => Promise<void> }> = ({ onLogin }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Please enter a token');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onLogin(token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-auth-container">
      <h1 className="app-auth-title">PayPrompt</h1>
      <div className="app-auth-form">
        <h2 className="app-auth-form-title">Sign In</h2>
        {error && (
          <div className="app-auth-error" style={{ color: 'red', marginBottom: '15px', padding: '10px', background: '#ffe6e6', borderRadius: '4px' }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="app-auth-input-group">
            <label className="app-auth-label">
              Token:
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="app-auth-input"
                placeholder="Enter your bearer token"
                disabled={loading}
              />
            </label>
          </div>

          <button
            type="submit"
            className="app-auth-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>

      <div className="app-auth-info">
        <h3>How to use:</h3>
        <ol>
          <li>Obtain a valid bearer token from the PayPrompt system</li>
          <li>Enter the token in the field above</li>
          <li>Click "Sign In" to connect to the API</li>
          <li><strong>Webview Mode:</strong> Add ?standalone=true&screen=[screen]&token=[token] to URL</li>
        </ol>
        <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          <p><strong>Webview URLs Examples:</strong></p>
          <ul>
            <li>Dashboard: ?standalone=true&screen=dashboard&token=YOUR_TOKEN</li>
            <li>Tasks: ?standalone=true&screen=tasks&token=YOUR_TOKEN</li>
            <li>Ledger: ?standalone=true&screen=ledger&token=YOUR_TOKEN</li>
            <li><em>Note: Internal navigation works in standalone mode (e.g., from Ledger to Withdrawal)</em></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Check URL parameters for standalone mode on initial render
  const searchParams = new URLSearchParams(window.location.search);
  const isStandaloneParam = searchParams.get('standalone') === 'true';
  const standaloneScreen = searchParams.get('screen') || 'dashboard';
  const standaloneToken = searchParams.get('token');

  // Handle login for normal mode
  const handleLogin = async (token: string) => {
    setLoginError(null);
    try {
      await initialize(token);
      const permissions = await getUserPermissions();
      setUserPermissions(permissions);
    } catch (err) {
      const errorMessage = (err as Error).message;
      setLoginError(errorMessage);
      throw new Error('Failed to initialize SDK: ' + errorMessage);
    }
  };

  // Check if we should render in standalone mode
  if (isStandaloneParam && standaloneToken) {
    return (
      <Router>
        <AppInitializer />
        <StandaloneApp token={standaloneToken} initialScreen={standaloneScreen} />
      </Router>
    );
  }

  // Normal mode - show login if not initialized
  if (!isInitialized()) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Normal authenticated app mode
  return (
    <Router>
      <AppInitializer />
      <AuthenticatedApp />
    </Router>
  );
};

export default App;